import type { ClockConfig } from "@ronin/contracts";
import { HOME_GRID_COLUMNS } from "@ronin/core";
import { cn } from "@ronin/ui";
import { useEffect, useState } from "react";
import { useTimeZone } from "../settings/use-settings.js";

/**
 * spec12 FR6: the time (24 h) and, on a tile at least a full row tall, the date in the user's
 * timezone. The text scales with the tile (container query units), so a clock fills its tile
 * at any size (ADR-0013). Where the height limits it, the time lines up with the grid edge the
 * tile touches (right edge: right-aligned), centred in between, so it never leaves a gap there.
 */
export function ClockWidget({
  config,
  x,
  w,
  h,
}: {
  config: ClockConfig;
  x: number;
  w: number;
  h: number;
}) {
  const timeZone = useTimeZone();
  const now = useNow(config.seconds ? 1000 : 10_000);
  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    ...(config.seconds && { second: "2-digit" }),
  }).format(now);
  const date = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(now);
  const withDate = config.date && h >= 4;
  const align =
    x === 0
      ? "items-start text-left"
      : x + w >= HOME_GRID_COLUMNS
        ? "items-end text-right"
        : "items-center text-center";

  return (
    <div
      className={cn(
        "flex h-full min-w-0 flex-col justify-center gap-[4cqh] px-2 [container-type:size]",
        align,
      )}
    >
      <time
        dateTime={now.toISOString()}
        className={cn(
          "leading-none font-semibold tracking-tight whitespace-nowrap text-foreground tabular-nums",
          // As large as the tile allows: limited by its width (by the digits) and its height.
          config.seconds
            ? withDate
              ? "text-[min(21cqw,50cqh)]"
              : "text-[min(21cqw,75cqh)]"
            : withDate
              ? "text-[min(34cqw,50cqh)]"
              : "text-[min(34cqw,75cqh)]",
        )}
      >
        {time}
      </time>
      {withDate && (
        <p className="max-w-full truncate text-[min(7cqw,18cqh)] leading-none text-muted-foreground">
          {date}
        </p>
      )}
    </div>
  );
}

function useNow(intervalMs: number) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
}
